// ─── Filter framework: the adapter contract (f-02062) ─────────────
// A list adopts the framework by describing itself once — a
// ListFilterDescriptor keyed by listId (the NSToolbar-identifier equivalent) —
// and registering it. Everything else (bar, pills, saved filters, layout,
// sharing, history, icons, the customizer) is generic and reads only this.
//
// mode 'server': the list's own row endpoint compiles the definition to SQL
// (apps/api/src/services/filters). It is the ONLY supported mode today.
// mode 'client' is RESERVED and NOT YET SUPPORTED: the web pickers, the bar's
// value labels and the Advanced builder's live count always call the server
// values / count routes, which answer 404 unknown_list for a list without a
// server entity. Supporting it needs an in-memory values + count adapter in
// apps/web/src/lib/filters (evaluate.ts already has the row semantics). The
// contract test fails while any registered list uses it.
// ─── Registry ─────────────────────────────────────────────────────
const LISTS = new Map();
/** Register (or replace) a list descriptor. Throws on a malformed descriptor
 *  so a broken adapter fails at import time, not on a user's screen. */
export function registerFilterList(descriptor) {
    const problems = checkFilterListDescriptor(descriptor);
    if (problems.length)
        throw new Error(`Invalid filter list descriptor "${descriptor.listId}": ${problems.join('; ')}`);
    LISTS.set(descriptor.listId, descriptor);
    return descriptor;
}
export function getFilterList(listId) {
    return LISTS.get(listId);
}
export function listFilterLists() {
    return [...LISTS.values()];
}
export function getFilterField(descriptor, key) {
    return descriptor.fields.find((f) => f.key === key);
}
export function isFilterAvailable(availability, features) {
    if (!availability)
        return true;
    const f = features ?? {};
    if (availability.requiresFeature && !f[availability.requiresFeature])
        return false;
    if (availability.hiddenWhenFeature && f[availability.hiddenWhenFeature])
        return false;
    return true;
}
/** Structural self-check of a descriptor (used by registerFilterList and the
 *  contract test). Returns human-readable problems; empty = fine. */
export function checkFilterListDescriptor(d) {
    const out = [];
    if (!/^[a-z][a-z0-9-]{0,63}$/.test(d.listId))
        out.push('listId must be lowercase kebab, max 64');
    const keys = new Set();
    for (const f of d.fields) {
        if (keys.has(f.key))
            out.push(`duplicate field ${f.key}`);
        keys.add(f.key);
        if (!/^[a-zA-Z][a-zA-Z0-9]*$/.test(f.key))
            out.push(`field key ${f.key} must be alphanumeric`);
        if (!f.ops.length)
            out.push(`field ${f.key} has no operators`);
        if (f.ops.includes('in_category') && !f.categories?.length)
            out.push(`field ${f.key} offers in_category without categories`);
        if (f.values === 'static' && !f.staticValues?.length)
            out.push(`field ${f.key} is static without staticValues`);
    }
    const sorts = new Set(d.sorts.map((s) => s.key));
    if (!sorts.has(d.defaultSort))
        out.push(`defaultSort ${d.defaultSort} is not a sort`);
    const std = new Set();
    for (const s of d.standardFilters) {
        if (std.has(s.key))
            out.push(`duplicate standard filter ${s.key}`);
        std.add(s.key);
        for (const c of collectFields(s.definition))
            if (!keys.has(c))
                out.push(`standard filter ${s.key} uses unknown field ${c}`);
    }
    if (!std.has('all'))
        out.push('a list needs the std:all standard filter');
    for (const id of d.bar.movableButNotRemovable) {
        if (!d.bar.palette.includes(id))
            out.push(`unremovable item ${id} missing from palette`);
    }
    for (const id of d.bar.palette) {
        if (id.startsWith('quick:')) {
            const f = d.fields.find((x) => x.key === id.slice(6));
            if (!f || !f.quick)
                out.push(`palette item ${id} has no quick field`);
        }
    }
    return out;
}
function collectFields(def) {
    const out = [];
    const walk = (n) => {
        for (const c of n.children) {
            if (c.kind === 'cond')
                out.push(c.field);
            else
                walk(c);
        }
    };
    walk(def.root);
    return out;
}
//# sourceMappingURL=registry.js.map